/**
 * Add organization to events constants
 */

/** Wire values are camelCase first letter. Archived events are never returned. */
export const eventStatus = {
  live: 'live',
  completed: 'completed'
} as const;
export type EventStatus = (typeof eventStatus)[keyof typeof eventStatus];

/** Wire value of the payment platform type an event campaign needs. */
export const paymentPlatformType = {
  creditCardForm: 'creditCardForm'
} as const;

/** A single organization event is treated as possibly exclusive to that organization. */
export const EXCLUSIVE_ORGANIZATION_COUNT = 1;
/** Below this many organizations the count is highlighted as low. */
export const LOW_ORGANIZATION_COUNT = 10;

export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Events sent per request. Core accepts up to 50, but each event is several
 * writes, so small batches keep every request well inside the gateway timeout
 * and let the button report progress.
 */
export const ADD_BATCH_SIZE = 10;

/** How long the success snackbar stays up unless dismissed. */
export const SNACKBAR_DURATION_MS = 8000;

const base = 'systemSettings.addOrganizationToEvents';

export const i18nKeys = {
  TITLE: `${base}.title`,
  DESCRIPTION: `${base}.description`,
  SEARCH: {
    LABEL: `${base}.search.label`,
    PLACEHOLDER: `${base}.search.placeholder`,
    HINT: `${base}.search.hint`,
    NO_RESULTS: `${base}.search.noResults`,
    FAILED: `${base}.search.failed`,
    COLUMN_NAME: `${base}.search.columnName`,
    COLUMN_ONBOARDING: `${base}.search.columnOnboarding`,
    COLUMN_EVENTS: `${base}.search.columnEvents`,
    PAYMENT_READY: `${base}.search.paymentReady`,
    PAYMENT_MISSING: `${base}.search.paymentMissing`,
    EVENTS_COUNT: `${base}.search.eventsCount`,
    NO_EVENTS: `${base}.search.noEvents`,
    SELECT: `${base}.search.select`,
    SELECT_LABEL: `${base}.search.selectLabel`
  },
  ORGANIZATION: {
    NOT_FOUND: `${base}.organization.notFound`,
    LOAD_FAILED: `${base}.organization.loadFailed`,
    NO_PAYMENT_PLATFORM: `${base}.organization.noPaymentPlatform`,
    BACK_TO_SEARCH: `${base}.organization.backToSearch`
  },
  EVENTS: {
    STEP: `${base}.events.step`,
    TITLE: `${base}.events.title`,
    SUBTITLE: `${base}.events.subtitle`,
    ALREADY_IN: `${base}.events.alreadyIn`,
    CHANGE_ORGANIZATION: `${base}.events.changeOrganization`,
    SEARCH_LABEL: `${base}.events.searchLabel`,
    SEARCH_PLACEHOLDER: `${base}.events.searchPlaceholder`,
    COUNTRY: `${base}.events.country`,
    STATE: `${base}.events.state`,
    ANY_COUNTRY: `${base}.events.anyCountry`,
    ANY_STATE: `${base}.events.anyState`,
    UPCOMING_ONLY: `${base}.events.upcomingOnly`,
    HIDE_CLOSED: `${base}.events.hideClosed`,
    HIDE_SINGLE_ORGANIZATION: `${base}.events.hideSingleOrganization`,
    SHOW_ALREADY_ADDED: `${base}.events.showAlreadyAdded`,
    SHOWING: `${base}.events.showing`,
    SELECT_ALL: `${base}.events.selectAll`,
    SELECT_EVENT: `${base}.events.selectEvent`,
    COLUMN_NAME: `${base}.events.columnName`,
    COLUMN_LOCATION: `${base}.events.columnLocation`,
    COLUMN_ORGANIZATIONS: `${base}.events.columnOrganizations`,
    COLUMN_DATE: `${base}.events.columnDate`,
    EXCLUSIVE: `${base}.events.exclusive`,
    LOW: `${base}.events.low`,
    LOW_COUNT: `${base}.events.lowCount`,
    ALREADY_ADDED: `${base}.events.alreadyAdded`,
    SINGLE_ORGANIZATION_PICKED: `${base}.events.singleOrganizationPicked`,
    CLOSED: `${base}.events.closed`,
    NONE: `${base}.events.none`,
    LOAD_FAILED: `${base}.events.loadFailed`,
    RETRY: `${base}.events.retry`,
    CANCEL: `${base}.events.cancel`,
    SUBMIT: `${base}.events.submit`,
    SUBMITTING: `${base}.events.submitting`,
    SUBMIT_FAILED: `${base}.events.submitFailed`
  },
  EXCLUSIVE_MODAL: {
    TITLE: `${base}.exclusiveModal.title`,
    BODY: `${base}.exclusiveModal.body`,
    QUESTION: `${base}.exclusiveModal.question`,
    CONFIRM: `${base}.exclusiveModal.confirm`,
    CANCEL: `${base}.exclusiveModal.cancel`
  },
  DONE: {
    TITLE: `${base}.done.title`,
    ADDED: `${base}.done.added`,
    UNCHANGED: `${base}.done.unchanged`,
    SKIPPED: `${base}.done.skipped`
  }
};
